import type { CommissionCalculationWithParts } from '@/services/commissions';

/**
 * W0-A decision 7 — "Total" keeps the full split, and the screen shows each
 * part so the sum is visible.
 *
 * `total_commission` is what the insurer pays for the policy. It is split into
 * platform + broker (retained) + POSP + super-POSP + advertiser shares, and the
 * Calculations table used to show Broker and POSP beside a Total that was
 * visibly bigger than the two — which read as money going missing, or as a
 * double count, depending on who was looking.
 *
 * The rules:
 *  - only non-zero shares are listed;
 *  - the channel payout is owed OUT OF the broker share (it does not reduce
 *    `broker_commission`), so it is shown as "of which", never added;
 *  - if the listed shares do not reach the total (rules, penalties, older
 *    rows), the gap is shown as its own line rather than hidden, so the parts
 *    always add up to the figure printed above them.
 */

export interface TotalPart {
  key: string;
  label: string;
  amount: number;
}

export interface TotalBreakdown {
  total: number;
  parts: TotalPart[];
  /** Part of the broker share owed onward to a non-POSP channel. */
  channelOfBroker: { name: string; amount: number } | null;
}

function amount(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Paise-safe: compare and subtract in whole paise, not floats. */
function paise(n: number): number {
  return Math.round(n * 100);
}

export function commissionTotalParts(calc: CommissionCalculationWithParts): TotalBreakdown {
  const total = amount(calc.total_commission);
  const candidates: TotalPart[] = [
    { key: 'broker', label: 'Broker', amount: amount(calc.broker_commission) },
    { key: 'posp', label: 'POSP', amount: amount(calc.posp_commission) },
    { key: 'super_posp', label: 'Super-POSP', amount: amount(calc.super_posp_commission) },
    {
      key: 'advertiser',
      label: calc.advertiser_name ? `Advertiser (${calc.advertiser_name})` : 'Advertiser',
      amount: amount(calc.advertiser_commission),
    },
    { key: 'platform', label: 'Platform', amount: amount(calc.platform_commission) },
  ];
  const parts = candidates.filter((p) => paise(p.amount) !== 0);

  const gap = paise(total) - parts.reduce((sum, p) => sum + paise(p.amount), 0);
  if (gap !== 0 && parts.length > 0) {
    parts.push({ key: 'other', label: 'Other adjustments', amount: gap / 100 });
  }

  const channelAmount = amount(calc.channel_payout_amount);
  const channelOfBroker =
    paise(channelAmount) !== 0
      ? { name: calc.channel_payout_name || 'channel', amount: channelAmount }
      : null;

  return { total, parts, channelOfBroker };
}
